import React from 'react';
import { motion } from 'framer-motion';
import '../styles/Pages.css';
import { PrivacyHero } from '../components/PageHeroes';

// Simple slugify for anchor links — keeps the data array plain
// (title/content only) instead of hand-maintaining ids that could
// drift out of sync with the titles.
const slugify = (title) =>
  title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const LAST_UPDATED = '19 June 2026';

const Privacy = () => {
  const sections = [
    {
      title: 'Information We Collect',
      content: 'We collect information you provide directly to us, such as when you create an account, use our services, or contact us. This includes your name, email address, company information, and the data you enter to use our services — including payroll and employee records, financial and banking data, customer and supplier details, uploaded documents, and marketing subscriber information.'
    },
    {
      title: 'How We Use Your Information',
      content: 'We use the information we collect to provide, maintain, and improve our services, process transactions, send transactional and promotional communications, and comply with legal obligations.'
    },
    {
      title: 'Data Security',
      content: 'We implement comprehensive security measures including encryption, secure servers, and access controls to protect your personal information from unauthorized access, alteration, or deletion.'
    },
    {
      title: 'Your Rights',
      content: 'Under UK and EU privacy laws, you have the right to access, correct, or delete your personal data. You can also object to processing or request data portability. Contact us to exercise these rights.'
    },
    {
      title: 'Cookies',
      content: 'We use cookies to improve your experience on our website. You can control cookie settings in your browser preferences. See our Cookie Policy for more details.'
    },
    {
      title: 'Third-Party Sharing',
      content: 'We do not sell, trade, or share your personal information with third parties except as required by law or with your explicit consent. We use service providers who are bound by confidentiality agreements.'
    },
    {
      title: 'Data Retention',
      content: 'We retain your personal information for as long as necessary to provide our services and comply with legal obligations. You can request deletion of your data anytime.'
    },
    {
      title: 'Contact Us',
      content: 'If you have privacy concerns or questions, contact our Data Protection Officer at thomas@effectivestaff.co.uk or write to our UK office.'
    }
  ];

  return (
    <div className="page-container">
      <PrivacyHero />

      <section className="section-legal">
        <div className="container legal-layout">
          {/* Sticky jump nav — earns its place here because the page
              is a genuine ordered reference, unlike a marketing page. */}
          <nav className="legal-toc" aria-label="Privacy policy sections">
            <span className="legal-toc-label">On this page</span>
            <ul>
              {sections.map((section, idx) => (
                <li key={idx}>
                  <a href={`#${slugify(section.title)}`}>
                    <span className="legal-toc-number">{String(idx + 1).padStart(2, '0')}</span>
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <motion.div
            className="legal-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="legal-updated">Last updated: {LAST_UPDATED}</p>

            <p className="intro">
              Effective Global Staff ("we", "us", "our") operates the Effective Global Staff website and application.
              This page informs you of our policies regarding the collection, use, and disclosure of personal
              data when you use our Service and the choices you have associated with that data.
            </p>

            {sections.map((section, idx) => (
              <motion.div
                key={idx}
                id={slugify(section.title)}
                className="legal-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
              >
                <h2>
                  <span className="legal-section-number">{String(idx + 1).padStart(2, '0')}</span>
                  {section.title}
                </h2>
                <p>{section.content}</p>
              </motion.div>
            ))}

            <motion.div
              className="legal-footer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3>Changes to This Privacy Policy</h3>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by
                posting the new Privacy Policy on this page and updating the "Last updated" date above.
              </p>
              <p>
                <strong>Questions?</strong> Contact us at <a href="mailto:thomas@effectivestaff.co.uk">thomas@effectivestaff.co.uk</a>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;